import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowRight, Check, HelpCircle } from 'lucide-react';
import platforms from '../data/platforms.json';
import seoContent from '../content/seo';
import { useLanguage } from '../contexts/LanguageContext';
import { tr } from '../utils/i18n';
import { routePath, routeUrl, SEO_PAGE_KEYS, SITE_URL } from '../i18n/routes';

const SeoLandingPage = ({ pageKey }) => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const l = (text) => tr(currentLanguage, text);
  const content = seoContent[pageKey]?.[currentLanguage] || seoContent[pageKey]?.en;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pageKey, currentLanguage]);

  if (!content) return null;

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: content.faq.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer }
      }))
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'OptimHub', item: `${SITE_URL}/${currentLanguage}` },
        { '@type': 'ListItem', position: 2, name: content.h1, item: routeUrl(pageKey, currentLanguage) }
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: content.meta.title,
      numberOfItems: platforms.length,
      itemListElement: platforms.map((platform, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: platform.name
      }))
    }
  ];

  const relatedKeys = SEO_PAGE_KEYS.filter((key) => key !== pageKey);

  return (
    <div className="min-h-screen bg-white" data-testid={`seo-page-${pageKey}`}>
      <SEOHead
        title={content.meta.title}
        description={content.meta.description}
        keywords={content.meta.keywords}
        routeKey={pageKey}
        lang={currentLanguage}
        type="article"
        schemas={schemas}
      />
      <Header />

      {/* Hero */}
      <section className="pt-28 pb-14 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {content.eyebrow && (
            <Badge className="bg-blue-100 text-blue-800 mb-6" data-testid="seo-eyebrow">
              {content.eyebrow}
            </Badge>
          )}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
            {content.h1}
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed mb-8" data-testid="seo-intro">
            {content.intro}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              data-testid="seo-cta-primary"
              onClick={() => navigate(routePath('publishers', currentLanguage))}
              className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-base rounded-full transition-all duration-300 transform hover:scale-105"
            >
              {l('Demander mon accès API')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              data-testid="seo-cta-secondary"
              onClick={() => navigate(routePath('home', currentLanguage), { state: { scrollTo: 'contact' } })}
              className="px-8 py-6 text-base rounded-full border-gray-300"
            >
              {l('Contacter notre équipe')}
            </Button>
          </div>
        </div>
      </section>

      {/* Sections */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {content.sections.map((section, index) => (
            <section key={section.h2} data-testid={`seo-section-${index}`}>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 leading-snug">
                {section.h2}
              </h2>
              {section.paragraphs?.map((paragraph, i) => (
                <p key={i} className="text-gray-600 leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))}
              {section.bullets?.length > 0 && (
                <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {section.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-3 text-gray-700">
                      <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </article>

      {/* Platforms */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            {l('Les plateformes accessibles via une seule API')}
          </h2>
          <p className="text-gray-600 leading-relaxed mb-10 max-w-3xl">
            {l("Via l'API OptimHub, vous accédez aux annonceurs et aux offres disponibles sur chacune de ces plateformes de monétisation. Les plateformes, les annonceurs et les conditions accessibles dépendent de votre activité et sont précisés lors de la prise de contact.")}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {platforms.map((platform) => (
              <div
                key={platform.name}
                className="h-24 rounded-xl border border-gray-100 bg-white flex flex-col items-center justify-center gap-2 p-3"
              >
                {platform.logo && (
                  <img
                    src={platform.logo}
                    alt={platform.name}
                    loading="lazy"
                    className="max-h-7 max-w-[80%] object-contain grayscale"
                  />
                )}
                <span className="text-[11px] font-medium text-gray-600 text-center leading-tight">
                  {platform.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10">
            {l('Questions fréquentes')}
          </h2>
          <div className="space-y-6">
            {content.faq.map((item, index) => (
              <div key={item.question} data-testid={`seo-faq-${index}`} className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  {item.question}
                </h3>
                <p className="text-gray-600 leading-relaxed pl-8">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + internal links */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            {content.cta?.title || l('Connectez votre audience à de nouvelles opportunités.')}
          </h2>
          <p className="text-gray-300 leading-relaxed mb-8">{content.cta?.text}</p>
          <div className="flex flex-col sm:flex-row gap-4 mb-14">
            <Button
              data-testid="seo-cta-bottom"
              onClick={() => navigate(routePath('publishers', currentLanguage))}
              className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-base rounded-full"
            >
              {l('Demander mon accès API')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          <h3 className="text-sm font-semibold uppercase tracking-widest text-cyan-400 mb-5">
            {content.relatedTitle || l('À lire également')}
          </h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {relatedKeys.map((key) => (
              <li key={key}>
                <a
                  href={routePath(key, currentLanguage)}
                  data-testid={`seo-related-${key}`}
                  className="text-gray-200 hover:text-white underline underline-offset-4 decoration-gray-600 hover:decoration-cyan-400 transition-colors duration-200"
                >
                  {seoContent[key]?.[currentLanguage]?.h1 || seoContent[key]?.en?.h1}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SeoLandingPage;
